import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ClientSidebar } from './ClientSidebar'
import { DarkModeToggle } from '@hpanel/web-common/components/DarkModeToggle'
import { useAuth } from '@hpanel/web-common/contexts/AuthContext'
import { Menu, Search, Bell, ChevronRight } from '@hpanel/web-common/components/icons'
import { ImpersonationProvider } from '@hpanel/web-common/contexts/ImpersonationContext'
import { ImpersonationBanner } from '@hpanel/web-common/components/ImpersonationBanner'

/**
 * Main layout component for the client (tenant) portal.
 *
 * Mirrors the admin Layout chrome: sidebar with mobile toggle, top
 * bar with breadcrumb, search hint, bell, dark-mode toggle, and a
 * user-initials avatar. The breadcrumb derives the current page
 * label from the URL so individual pages don't need to wire it up.
 */

interface ClientLayoutProps {
  children: React.ReactNode
}

const titleCase = (s: string): string =>
  s
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

/** Friendly labels for path segments whose title-case reads badly. */
const CRUMB_LABELS: Record<string, string> = {
  domains: 'Domains',
  sites: 'Sites',
  request: 'Request a site',
  park: 'Park a domain',
  wordpress: 'WordPress',
  staging: 'Clone to staging',
  dns: 'DNS',
  sftp: 'SFTP',
  ssl: 'SSL',
  php: 'PHP',
}

interface Crumb {
  label: string
  to: string
}

/**
 * Trail for the top bar: every segment after /client, each linking to its own
 * prefix (the caller makes the last one plain text). Numeric id segments are
 * skipped; an id directly under /domains is the domain's Manage page.
 */
const breadcrumbTrail = (pathname: string): Crumb[] => {
  const segments = pathname.split('/').filter(Boolean)
  const rest = segments.slice(1) // drop "client"
  if (rest.length === 0) return [{ label: 'Dashboard', to: '/client' }]
  const crumbs: Crumb[] = []
  rest.forEach((seg, i) => {
    const to = '/' + segments.slice(0, i + 2).join('/')
    if (/^\d+$/.test(seg)) {
      if (i === rest.length - 1 && rest[i - 1] === 'domains') {
        crumbs.push({ label: 'Manage', to })
      }
      return
    }
    crumbs.push({ label: CRUMB_LABELS[seg] ?? titleCase(seg), to })
  })
  return crumbs
}

const initialsFor = (input?: string | null): string => {
  if (!input) return '·'
  const parts = input.trim().split(/[\s@.]+/).filter(Boolean)
  if (parts.length === 0) return '·'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

export const ClientLayout: React.FC<ClientLayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const location = useLocation()
  const { user } = useAuth()

  const toggleSidebar = () => setIsSidebarOpen((open) => !open)
  const closeSidebar = () => setIsSidebarOpen(false)
  React.useEffect(() => { setIsSidebarOpen(false) }, [location.pathname, location.search])
  const toggleRef = React.useRef<HTMLButtonElement>(null)
  const handleSidebarKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeSidebar()
      toggleRef.current?.focus()
    }
  }

  const crumbs = breadcrumbTrail(location.pathname)

  const initials = initialsFor(user?.username || user?.email)

  return (
    <ImpersonationProvider>
    <ImpersonationBanner />
    <div className="flex min-h-screen bg-white dark:bg-gray-950 transition-colors">
      <div
        id="navigation-sidebar"
        onKeyDown={handleSidebarKey}
        className={`
          fixed lg:static inset-y-0 left-0 z-40
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'visible translate-x-0' : 'invisible lg:visible -translate-x-full lg:translate-x-0'}
        `}
      >
        <ClientSidebar />
      </div>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-30 lg:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 px-4 sm:px-6 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={toggleSidebar}
                className="lg:hidden p-1.5 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Toggle sidebar"
                aria-expanded={isSidebarOpen}
                aria-controls="navigation-sidebar"
                ref={toggleRef}
              >
                <Menu className="h-5 w-5" strokeWidth={1.75} />
              </button>

              <nav
                aria-label="Breadcrumb"
                className="hidden sm:flex items-center gap-1.5 text-[13px] text-gray-500 dark:text-gray-400 min-w-0"
              >
                <span>Workspace</span>
                {crumbs.map((c, i) => {
                  const isLast = i === crumbs.length - 1
                  return (
                    <React.Fragment key={c.to}>
                      <ChevronRight
                        className="h-3.5 w-3.5 text-gray-400 dark:text-gray-600 flex-shrink-0"
                        strokeWidth={1.75}
                        aria-hidden="true"
                      />
                      {isLast ? (
                        <span
                          aria-current="page"
                          className="text-gray-900 dark:text-gray-100 font-medium truncate"
                        >
                          {c.label}
                        </span>
                      ) : (
                        <Link to={c.to} className="hover:text-gray-900 dark:hover:text-gray-100">
                          {c.label}
                        </Link>
                      )}
                    </React.Fragment>
                  )
                })}
              </nav>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="hidden md:flex items-center gap-2 h-8 px-2.5 border border-gray-200 dark:border-gray-800 rounded-md text-[12px] text-gray-500 dark:text-gray-500 hover:border-gray-300 dark:hover:border-gray-700 transition-colors w-[200px]"
                aria-label="Search"
              >
                <Search className="h-3.5 w-3.5" strokeWidth={1.75} />
                <span className="flex-1 text-left">Search</span>
                <kbd className="font-mono text-[10px] px-1 py-px border border-gray-200 dark:border-gray-700 rounded text-gray-400">
                  ⌘K
                </kbd>
              </button>

              <button
                type="button"
                className="h-8 w-8 rounded-md grid place-items-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Notifications"
              >
                <Bell className="h-4 w-4" strokeWidth={1.75} />
              </button>

              <DarkModeToggle />

              <div
                className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 grid place-items-center text-[11px] font-medium"
                aria-label={user?.username || user?.email || 'Account'}
                title={user?.username || user?.email || 'Account'}
              >
                {initials}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>
    </div>
    </ImpersonationProvider>
  )
}
